import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Typography, Modal, Form, Input, Checkbox, Select, Upload, Image as AntImage, Divider, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined, DeleteOutlined, EditOutlined, SearchOutlined, MinusCircleOutlined } from '@ant-design/icons';
import MaskedInput from 'antd-mask-input';
import { 
    useGetTourPackagesMutation, 
    useCreateTourPackageMutation, 
    useUpdateTourPackageMutation, 
    useDeleteTourPackageMutation, 
    useGetTagsMutation, 
    useGetInclusionsMutation,
    useUploadImagesMutation,
    useDeleteImageMutation
} from '../redux/services/apiSlice';

const { Title } = Typography;
const { Option } = Select;

const TourPackages = () => {
    const [getTours, { data, isLoading }] = useGetTourPackagesMutation();
    const [createTour] = useCreateTourPackageMutation();
    const [updateTour] = useUpdateTourPackageMutation();
    const [deleteTour] = useDeleteTourPackageMutation();
    const [getTags, { data: tags }] = useGetTagsMutation();
    const [getInclusions, { data: inclusions }] = useGetInclusionsMutation();
    const [uploadImages] = useUploadImagesMutation();
    const [deleteImage] = useDeleteImageMutation();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);

    // Table state
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [searchText, setSearchText] = useState('');
    const [sortOrder, setSortOrder] = useState('-createdAt');

    const fetchTours = useCallback((page = 1, limit = 10, search = '', sort = '-createdAt') => {
        getTours({ page, limit, search, sort });
    }, [getTours]);

    useEffect(() => {
        fetchTours(pagination.current, pagination.pageSize, searchText, sortOrder);
        getTags();
        getInclusions();
    }, [fetchTours, pagination.current, pagination.pageSize, searchText, sortOrder, getTags, getInclusions]);

    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
        if (sorter.field) {
            const order = sorter.order === 'ascend' ? sorter.field : `-${sorter.field}`;
            setSortOrder(order);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination({ ...pagination, current: 1 });
    };

    const handleEdit = (record) => {
        setEditingId(record._id);
        const formattedRecord = {
            ...record,
            tourItinerary: record.tourItinerary?.map(item => ({
                ...item,
                date: item.date ? dayjs(item.date) : null
            }))
        };
        form.setFieldsValue(formattedRecord);
        setFileList(record.images.map((img, index) => ({
            uid: index,
            name: img.name,
            status: 'done',
            url: img.url,
            response: { files: [{ url: img.url, name: img.name }] }
        })));
        setIsModalVisible(true);
    };

    const handleCustomRequest = async ({ file, onSuccess, onError }) => {
        const formData = new FormData();
        formData.append('images', file);
        try {
            const res = await uploadImages(formData).unwrap();
            onSuccess(res);
            message.success(`${file.name} uploaded successfully`);
        } catch (err) {
            onError(err);
            message.error(`${file.name} upload failed`);
        }
    };

    const handleRemoveFile = async (file) => {
        try {
            const filename = file.response?.files[0]?.name || file.name;
            await deleteImage(filename).unwrap();
            message.success('Image removed from server');
            return true;
        } catch (error) {
            message.error('Failed to remove image from server');
            return false;
        }
    };

    const handleFinish = async (values) => {
        try {
            const images = fileList
                .filter(file => file.status === 'done')
                .map((file, index) => {
                    const uploadedFile = file.response.files[0];
                    return {
                        url: uploadedFile.url,
                        name: uploadedFile.name,
                        isPrimary: index === 0
                    };
                });

            const finalValues = { 
                ...values, 
                images,
                tourItinerary: values.tourItinerary?.map(item => ({
                    ...item,
                    date: item.date ? item.date.toISOString() : undefined
                }))
            };

            if (editingId) {
                await updateTour({ id: editingId, ...finalValues }).unwrap();
                message.success('Tour updated');
            } else {
                await createTour(finalValues).unwrap();
                message.success('Tour created');
            }
            setIsModalVisible(false);
            setEditingId(null);
            form.resetFields();
            setFileList([]);
            fetchTours(pagination.current, pagination.pageSize, searchText, sortOrder);
        } catch (error) {
            message.error('Operation failed');
        }
    };

    const handleDeleteTour = async (record) => {
        try {
            if (record.images && record.images.length > 0) {
                await Promise.all(record.images.map(img => deleteImage(img.name).unwrap()));
            }
            await deleteTour(record._id).unwrap();
            message.success('Tour and images deleted');
            fetchTours(pagination.current, pagination.pageSize, searchText, sortOrder);
        } catch (error) {
            message.error('Delete failed');
        }
    };

    const columns = [
        { 
            title: 'Image', 
            dataIndex: 'images', 
            key: 'images',
            render: (images) => images && images.length > 0 ? <AntImage src={images[0].url} width={50} height={50} style={{ objectFit: 'cover', borderRadius: 4 }} /> : 'No Image'
        },
        { title: 'Name', dataIndex: 'tourPackageName', key: 'tourPackageName', sorter: true },
        { title: 'Duration', dataIndex: 'tourDuration', key: 'tourDuration' },
        { title: 'Price', dataIndex: 'pricePerPerson', key: 'pricePerPerson', sorter: true },
        { title: 'With Flight', dataIndex: 'withFlightPricePerPerson', key: 'withFlightPricePerPerson' },
        { title: 'Without Flight', dataIndex: 'withoutFlightPricePerPerson', key: 'withoutFlightPricePerPerson' },
        { title: 'Hotel Stars', dataIndex: 'hotelTypes', key: 'hotelTypes', render: (stars) => stars ? `${stars}★` : '-' },
        { title: 'City', dataIndex: 'city', key: 'city', sorter: true },
        { title: 'Popularity', dataIndex: 'popularity', key: 'popularity', sorter: true },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
                    <Popconfirm title="Delete?" onConfirm={() => handleDeleteTour(record)}>
                        <Button danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>Tour Packages</Title>
                <Space>
                    <Input.Search
                        placeholder="Search tours..."
                        onSearch={handleSearch}
                        style={{ width: 300 }}
                        enterButton={<SearchOutlined />}
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingId(null); form.resetFields(); setFileList([]); setIsModalVisible(true); }}>Create Tour</Button>
                </Space>
            </div>
            
            <Table 
                dataSource={data?.tours || []} 
                columns={columns} 
                rowKey="_id" 
                loading={isLoading}
                pagination={{
                    current: data?.currentPage || 1,
                    pageSize: pagination.pageSize,
                    total: data?.totalItems || 0,
                    showSizeChanger: true
                }}
                onChange={handleTableChange}
            />

            <Modal
                title={editingId ? "Edit Tour" : "Create Tour"}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                width={1000}
                style={{ top: 20 }}
            >
                <Form form={form} onFinish={handleFinish} layout="vertical">
                    <Divider orientation="left">General Information</Divider>
                    <Form.Item name="tourPackageName" label="Tour Package Name" rules={[{ required: true }]}><Input /></Form.Item>
                    
                    <Form.Item label="Tour Images">
                        <Upload
                            listType="picture-card"
                            fileList={fileList}
                            customRequest={handleCustomRequest}
                            onRemove={handleRemoveFile}
                            onChange={({ fileList }) => setFileList(fileList)}
                            multiple
                        >
                            {fileList.length >= 10 ? null : (
                                <div>
                                    <PlusOutlined />
                                    <div style={{ marginTop: 8 }}>Upload</div>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>

                    <Form.Item name="tourDescription" label="Tour Description" rules={[{ required: true }]}><Input.TextArea rows={4} /></Form.Item>
                    
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item name="tourDuration" label="Duration" rules={[{ required: true }]}>
                                <MaskedInput mask="0N 0D" placeholder="4N 5D" />
                            </Form.Item>
                        </Col>
                        <Col span={6}><Form.Item name="pricePerPerson" label="Base Price" rules={[{ required: true }]}><Input /></Form.Item></Col>
                        <Col span={6}><Form.Item name="withFlightPricePerPerson" label="Price With Flight"><Input type="number" /></Form.Item></Col>
                        <Col span={6}><Form.Item name="withoutFlightPricePerPerson" label="Price Without Flight"><Input type="number" /></Form.Item></Col>
                    </Row>
                    
                    <Row gutter={16}>
                        <Col span={5}><Form.Item name="country" label="Country" rules={[{ required: true }]}><Input /></Form.Item></Col>
                        <Col span={5}><Form.Item name="city" label="City" rules={[{ required: true }]}><Input /></Form.Item></Col>
                        <Col span={5}><Form.Item name="state" label="State" rules={[{ required: true }]}><Input /></Form.Item></Col>
                        <Col span={4}><Form.Item name="hotelTypes" label="Hotel Stars">
                            <Select placeholder="Select Stars">
                                <Option value={1}>1 Star</Option>
                                <Option value={2}>2 Star</Option>
                                <Option value={3}>3 Star</Option>
                                <Option value={4}>4 Star</Option>
                                <Option value={5}>5 Star</Option>
                            </Select>
                        </Form.Item></Col>
                        <Col span={5}><Form.Item name="popularity" label="Popularity Score"><Input type="number" /></Form.Item></Col>
                    </Row>
                    
                    <Divider orientation="left">Dynamic Lists</Divider>
                    <Form.Item name="tourInclusions" label="Inclusions">
                        <Select mode="multiple" placeholder="Select inclusions">
                            {inclusions?.map(i => <Option key={i.item}>{i.item}</Option>)}
                        </Select>
                    </Form.Item>

                    <Form.Item name="tourExclusions" label="Exclusions">
                        <Select open={false} mode="tags" placeholder="Type and press Enter to add exclusions" />
                    </Form.Item>
                    
                    <Form.Item name="commonTags" label="Common Tags">
                        <Select mode="multiple" placeholder="Select tags">
                            {tags?.map(t => <Option key={t.tagName}>{t.tagName}</Option>)}
                        </Select>
                    </Form.Item>

                    <Form.Item name="placesToVisit" label="Places To Visit">
                        <Select open={false} mode="tags" placeholder="Type and press Enter to add places" />
                    </Form.Item>

                    <Form.Item name="isFeatured" valuePropName="checked"><Checkbox>Is Featured Package</Checkbox></Form.Item>

                    <Divider orientation="left">Tour Itinerary</Divider>
                    <Form.List name="tourItinerary">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'dayNo']}
                                            rules={[{ required: true, message: 'Day No is required' }]}
                                        >
                                            <Input placeholder="Day No" style={{ width: 80 }} type="number" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'date']}
                                        >
                                            <DatePicker placeholder="Date" style={{ width: 130 }} />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'destination']}
                                            rules={[{ required: true, message: 'Destination is required' }]}
                                        >
                                            <Input placeholder="Destination" style={{ width: 200 }} />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'description']}
                                            rules={[{ required: true, message: 'Description is required' }]}
                                        >
                                            <Input.TextArea placeholder="Activity description" style={{ width: 500 }} autoSize />
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />
                                    </Space>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        Add Day to Itinerary
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Divider orientation="left">SEO & Policy</Divider>
                    <Form.Item name="metaTags" label="Meta Keywords (SEO)">
                        <Select open={false} mode="tags" placeholder="Type and press Enter to add SEO keywords" />
                    </Form.Item>
                    <Form.Item name="metaDescription" label="Meta Description (SEO)">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name="termsAndConditions" label="Terms & Conditions"><Input.TextArea rows={4} /></Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

// Layout helpers
const Row = ({ gutter, children }) => <div style={{ display: 'flex', gap: gutter }}>{children}</div>;
const Col = ({ span, children }) => <div style={{ flex: span }}>{children}</div>;

export default TourPackages;
